import {Divider, Grid, Typography} from '@material-ui/core';
import {fromJS, Map as IMap, List} from 'immutable';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {GridRow, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {removeQuoteFormErrorEntries, removeQuoteFormErrorEntry, updateQuoteFormErrors} from 'Quoting/ducks/createQuote';
import {createEisTypeObject, safeRenderValue, VALUE_NOT_AVAILABLE} from 'Quoting/utils/utilities';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {
  GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP,
  GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID,
  GPLUCoverageCode,
} from '../../../PlanTemplates/GWL/gpluConstants';
import {createSpouseWLCoverageRangeValues, createSpouseWLCoverageSingleValue} from '../../../PlanTemplates/GWL/Plan1';
import {
  getAmountValuePath,
  getGPLUSpouseBenefitSchedulePropertyPaths,
  getParentPath,
  spouseTypeInfo,
} from '../PropertyPaths';

export const GPLUSpouseBenefitScheduleBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();

  const {selectedPlan, defaultPlanValues, selectedCoverageIndex} = useSelector(
    (state: ReducerState) => state.quoteForm
  );
  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;
  const defaultValues = defaultPlanValues?.get(selectedPlanCd)?.get(GPLUCoverageCode.SPOUSE_WHOLE_LIFE);
  const dispatch = useDispatch();
  const benefitSchedules: List<IMap<string, any>> = getQuoteFieldValue(
    `${getParentPath(selectedPlan?.planIndex, selectedCoverageIndex)}.benefitSchedules`
  );

  const benefitScheduleKey: any = getQuoteFieldValue(
    `${getParentPath(selectedPlan?.planIndex, selectedCoverageIndex)}.benefitSchedule._key`
  );
  const propertyPaths = benefitSchedules
    ? (List.isList(benefitSchedules) ? benefitSchedules.toArray() : benefitSchedules).map((obj, index) => {
        return {
          ...getGPLUSpouseBenefitSchedulePropertyPaths(
            selectedPlan?.planIndex,
            selectedCoverageIndex,
            index,
            obj.get('spouseType')
          ),
          guaranteedIssueAmount: `${getParentPath(
            selectedPlan?.planIndex,
            selectedCoverageIndex
          )}.underwriting.spouseUnderwritingAmounts[${index}].guaranteedIssueAmount`,
        };
      })
    : [];

  const [rootId, parentId] = [benefitScheduleKey?.get('rootId'), benefitScheduleKey?.get('id')];

  const handleSpecifiedAmounts = (
    newValues: string[],
    lookup: Map<string, IMap<string, any>>,
    objPaths: any,
    formFieldId: string,
    spouseType: string
  ) => {
    const updatedMultipleValues = List(
      newValues.map(amount => {
        const key = amount;
        return (
          lookup.get(key) ??
          fromJS({
            ...createEisTypeObject('GPLUBenefitScheduleMultipleValues'.concat(objPaths.suffix), rootId, parentId),
            specifiedAmount: Number(key),
          })
        );
      })
    );
    const numericValues = newValues.map(v => Number(v)).filter(v => !Number.isNaN(v));
    const minVal = numericValues.length ? Math.min(...numericValues) : undefined;
    const maxVal = numericValues.length ? Math.max(...numericValues) : undefined;

    updateQuoteFieldValues([
      {path: objPaths.multipleValues, newValue: updatedMultipleValues},
      {path: getAmountValuePath(objPaths.minDeathBenefitAllowance), newValue: minVal},
      {path: getAmountValuePath(objPaths.maxDeathBenefitAllowance), newValue: maxVal},
    ]);
    const guaranteedIssueAmount = getQuoteFieldValue(getAmountValuePath(objPaths.guaranteedIssueAmount));

    if (!(updatedMultipleValues.size > 0)) {
      dispatch(
        updateQuoteFormErrors({
          [formFieldId]: `'Specified Benefit Amounts' is mandatory under ${
            GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[GPLUCoverageCode.SPOUSE_WHOLE_LIFE]
          }.`,
        })
      );
    } else if (updatedMultipleValues.size > 0 && !updatedMultipleValues.some(v => v.get('specifiedAmount') > Number(guaranteedIssueAmount))) {
      dispatch(removeQuoteFormErrorEntry(formFieldId));
    }
  };

  const handleBenefitTypeChange = (newType: string, obj: any) => {
    const giAmount = getQuoteFieldValue<number>(getAmountValuePath(obj.guaranteedIssueAmount));
    if (newType === 'SingleValue') {
      updateQuoteFieldValues([
        {path: obj.benefitTypeCd, newValue: newType},
        {path: obj.multipleValues, newValue: List()},
        {path: obj.rangeValues, newValue: undefined},
        {
          path: obj.singleValue,
          newValue: IMap(createSpouseWLCoverageSingleValue(defaultValues, rootId, parentId, obj.suffix, giAmount)),
        },
      ]);
    } else if (newType === 'RangeValues') {
      updateQuoteFieldValues([
        {path: obj.benefitTypeCd, newValue: newType},
        {path: obj.multipleValues, newValue: List()},
        {path: obj.singleValue, newValue: undefined},
        {
          path: obj.rangeValues,
          newValue: IMap(createSpouseWLCoverageRangeValues(defaultValues, rootId, parentId, obj.suffix, giAmount)),
        },
      ]);
    } else if (newType === 'MultipleValues') {
      updateQuoteFieldValues([
        {path: obj.benefitTypeCd, newValue: newType},
        {path: obj.singleValue, newValue: undefined},
        {path: obj.rangeValues, newValue: undefined},
      ]);
    }
    dispatch(
      removeQuoteFormErrorEntries([
        `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].benefitMaxAmount}__${selectedPlan?.planKey}`,
        `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].benefitMinAmount}__${selectedPlan?.planKey}`,
        `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].incrementAmount}__${selectedPlan?.planKey}`,
        `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].flatBenefitAmount}__${selectedPlan?.planKey}`,
        `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].specifiedBenefitAmount}__${selectedPlan?.planKey}`,
      ])
    );
  };

  const rows: GridRowProps[] = propertyPaths.map(obj => {
    const multipleValuesRaw: unknown = getQuoteFieldValue(obj.multipleValues);
    const multipleValuesSource = List.isList(multipleValuesRaw) ? multipleValuesRaw.toArray()
      : Array.isArray(multipleValuesRaw) ? multipleValuesRaw : [];
    const multipleValues: List<IMap<string, any>> = List(
      multipleValuesSource.map(value =>
        IMap.isMap(value) ? (value as IMap<string, any>) : IMap<string, any>(value as any)
      )
    );
    const lookup = new Map<string, IMap<string, any>>();
    const specifiedAmounts: string[] = multipleValues.map((m: IMap<string, any>) => {
        lookup.set(String(m.get('specifiedAmount')), m);
        return String(m.get('specifiedAmount'));
      })?.toArray().filter((value): value is string => Boolean(value));

    return {
      rowProps: {key: 'row-5e8a5fed-7788-4188-b68e-c684462de63f'},
      title: obj.title,
      items: [
        {
          key: `${obj.benefitTypeCd}`,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Benefit Type"
              id={obj.benefitTypeCd}
              value={getQuoteFieldValue(obj.benefitTypeCd)}
              onChange={e => handleBenefitTypeChange(String(e.target.value), obj)}
              dropdownVariant="AttributeValue"
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].benefitTypeCd}
              searchKey={`GPLUBenefitSchedule${obj.suffix}.benefitType${obj.suffix}Cd`}
              coverageCode={GPLUCoverageCode.SPOUSE_WHOLE_LIFE}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'd7525833-3637-439a-bfe9-1af2ee113b74',
          hidden: getQuoteFieldValue(obj.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Benefit Amount"
              id={obj.benefitMinAmount}
              value={getQuoteFieldValue(getAmountValuePath(obj.benefitMinAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(obj.benefitMinAmount), newValue: e.target.value},
                  {path: getAmountValuePath(obj.minDeathBenefitAllowance), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].benefitMinAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '74e9f486-94dc-43e1-aba0-262d15ad76c0',
          hidden: getQuoteFieldValue(obj.benefitTypeCd) !== 'MultipleValues',
          element: (
            <QuoteFormElement
              elementType="MultiSelect"
              label="Specified Benefit Amounts"
              id={obj.multipleValues}
              value={specifiedAmounts}
              onChange={val =>
                handleSpecifiedAmounts(
                  val,
                  lookup,
                  obj,
                  `${GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].specifiedBenefitAmount}__${
                    selectedPlan?.planKey
                  }`,
                  spouseTypeInfo[obj.suffix.toUpperCase()].name
                )
              }
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].specifiedBenefitAmount}
              coverageCode={GPLUCoverageCode.SPOUSE_WHOLE_LIFE}
              dropdownVariant="AttributeValue"
              searchKey={`GPLUBenefitScheduleMultipleValues${obj.suffix}.specifiedAmount`}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '55d036d9-b6aa-4a1d-b612-ac8e0e6edbf3',
          hidden: getQuoteFieldValue(obj.benefitTypeCd) !== 'SingleValue',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Flat Benefit Amount"
              id={obj.flatBenefitAmount}
              value={getQuoteFieldValue(getAmountValuePath(obj.flatBenefitAmount))}
              defaultValue={getQuoteFieldValue(getAmountValuePath(obj.flatBenefitAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(obj.flatBenefitAmount), newValue: e.target.value},
                  {path: getAmountValuePath(obj.minDeathBenefitAllowance), newValue: e.target.value},
                  {path: getAmountValuePath(obj.maxDeathBenefitAllowance), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].flatBenefitAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'b6165956-6d9f-4bb8-b26f-fc52ba418551',
          hidden: getQuoteFieldValue(obj.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount"
              id={obj.benefitMaxAmount}
              value={getQuoteFieldValue(getAmountValuePath(obj.benefitMaxAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: getAmountValuePath(obj.benefitMaxAmount), newValue: e.target.value},
                  {path: getAmountValuePath(obj.maxDeathBenefitAllowance), newValue: e.target.value},
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].benefitMaxAmount}
              type="number"
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'bac581be-7124-40f1-9802-32b054a51940',
          hidden: getQuoteFieldValue(obj.benefitTypeCd) !== 'RangeValues',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Increment"
              id={obj.incrementAmount}
              value={getQuoteFieldValue(getAmountValuePath(obj.incrementAmount))}
              onChange={e => updateQuoteFieldValue(getAmountValuePath(obj.incrementAmount), e.target.value)}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID[obj.suffix.toUpperCase()].incrementAmount}
              type="number"
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '9d12b6c4-fba9-47eb-8031-7c11f720a19d',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Death Benefit-Option"
              id={obj.deathBenefitOptionCd}
              value={getQuoteFieldValue(obj.deathBenefitOptionCd)}
              onChange={e => updateQuoteFieldValue(obj.deathBenefitOptionCd, e.target.value)}
              dropdownVariant="AttributeValue"
              searchKey={`GPLUBenefitScheduleBenefitOptions${obj.suffix}.deathBenefitOptionCd`}
              coverageCode={GPLUCoverageCode.SPOUSE_WHOLE_LIFE}
              required
              disabled
            />
          ),
        },
      ],
    };
  });

  return (
    <FormWrapper title="Benefit Schedule" collapsible withSeparator expanded>
      <Grid container alignItems="center" style={{maxWidth: '100%'}}>
        {rows?.map(
          ({items, rowProps, title, hidden}, index) =>
            !hidden && (
              <React.Fragment key={rowProps?.key}>
                {title && (
                  <>
                    <Typography variant="body1" style={{paddingLeft: '15px'}} className="lbl-main">
                      {title}
                    </Typography>
                    <Grid item xs={12}>
                      <Divider style={{width: '100%', margin: '10px 0', color: '#2F3847'}} />
                    </Grid>
                  </>
                )}
                <GridRow items={items} rowProps={rowProps} />
              </React.Fragment>
            )
        )}
      </Grid>
    </FormWrapper>
  );
};
